import React from "react";
import { Col, Form, InputNumber, Row, Select, Checkbox } from "antd";
import {
  AllowanceType,
  DEFAULT_ALLOWANCES,
  normalizeAllowances,
  hasDiscountAllowance,
  isDiscountOnlyAllowanceSet,
} from "../constants";
import * as texts from "../locales";

const { Option } = Select;

const VisibilityGroupAllowancesSection = ({
  form,
  freire,
  loading,
  loadingStations,
  stations = [],
  onAllowancesChange,
  onStationsDropdownVisibleChange,
  companies = [],
  loadingCompanies = false,
  selectedAllowances,
}) => {
  const { getFieldDecorator } = form;

  const allowancesSource =
    typeof selectedAllowances === "undefined"
      ? form.getFieldValue("allowances")
      : selectedAllowances;

  const allowances = normalizeAllowances(
    allowancesSource === undefined ? DEFAULT_ALLOWANCES : allowancesSource
  );

  const hasCustomDiscount = hasDiscountAllowance(allowances);
  const hasCustomReservation = allowances.includes(
    AllowanceType.CUSTOM_RESERVATION_TIME
  );

  const discountField = getFieldDecorator("percentageDiscountValue", {
    rules: [
      {
        validator: (_, value, callback) => {
          const currentAllowances = normalizeAllowances(
            form.getFieldValue("allowances") || DEFAULT_ALLOWANCES
          );

          if (!hasDiscountAllowance(currentAllowances)) {
            callback();
            return;
          }

          if (value === undefined || value === null) {
            callback(freire(texts.REQUIRED_FIELD));
            return;
          }

          callback();
        },
      },
    ],
  })(
    <InputNumber
      min={0}
      max={100}
      disabled={loading}
      style={{ width: "100%" }}
      placeholder={freire(texts.PERCENTAGE_DISCOUNT_PLACEHOLDER)}
    />
  );

  const reservationField = getFieldDecorator("reservationTime", {
    rules: [
      {
        validator: (_, value, callback) => {
          const currentAllowances = normalizeAllowances(
            form.getFieldValue("allowances") || DEFAULT_ALLOWANCES
          );

          if (!currentAllowances.includes(AllowanceType.CUSTOM_RESERVATION_TIME)) {
            callback();
            return;
          }

          if (value === undefined || value === null) {
            callback(freire(texts.REQUIRED_FIELD));
            return;
          }

          if (value <= 0) {
            callback(freire(texts.RESERVATION_TIME_MIN));
            return;
          }

          callback();
        },
      },
    ],
  })(
    <InputNumber
      min={1}
      disabled={loading}
      style={{ width: "100%" }}
      placeholder={freire(texts.RESERVATION_TIME_PLACEHOLDER)}
    />
  );

  return (
    <>
      <Row gutter={[16, 0]}>
        <Col span={24}>
          <Form.Item
            label={freire(texts.ALLOWANCES)}
            extra={
              allowances.includes(AllowanceType.SEMI_PUBLIC)
                ? freire(texts.ALLOWANCES_INTERNAL_FLEET_HINT)
                : freire(texts.ALLOWANCES_HINT)
            }
          >
            {getFieldDecorator("allowances", {
              initialValue: DEFAULT_ALLOWANCES,
            })(
              <Checkbox.Group onChange={onAllowancesChange}>
                <Row>
                  <Col span={12}>
                    <Checkbox value={AllowanceType.VISIBILITY} disabled={loading}>
                      {freire(texts.ALLOWANCE_VISIBILITY)}
                    </Checkbox>
                  </Col>
                  <Col span={12}>
                    <Checkbox value={AllowanceType.SEMI_PUBLIC} disabled={loading}>
                      {freire(texts.ALLOWANCE_SEMI_PUBLIC)}
                    </Checkbox>
                  </Col>
                  <Col span={12}>
                    <Checkbox
                      value={AllowanceType.CUSTOM_PERCENTAGE_PRICE}
                      disabled={loading}
                    >
                      {freire(texts.ALLOWANCE_CUSTOM_PERCENTAGE_PRICE)}
                    </Checkbox>
                  </Col>
                  <Col span={12}>
                    <Checkbox
                      value={AllowanceType.CUSTOM_RESERVATION_TIME}
                      disabled={loading}
                    >
                      {freire(texts.ALLOWANCE_CUSTOM_RESERVATION_TIME)}
                    </Checkbox>
                  </Col>
                </Row>
              </Checkbox.Group>
            )}
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[16, 0]} style={{ display: hasCustomDiscount ? undefined : "none" }}>
        <Col span={12}>
          <Form.Item
            label={freire(texts.PERCENTAGE_DISCOUNT)}
            extra={freire(texts.PERCENTAGE_DISCOUNT_HELP)}
          >
            {discountField}
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[16, 0]} style={{ display: hasCustomReservation ? undefined : "none" }}>
        <Col span={12}>
          <Form.Item
            label={freire(texts.RESERVATION_TIME)}
            extra={freire(texts.RESERVATION_TIME_HELP)}
          >
            {reservationField}
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[16, 0]}>
        <Col span={24}>
          <Form.Item label={freire(texts.STATIONS)}>
            {getFieldDecorator("stations", {
              rules: [
                {
                  validator: (_, value, callback) => {
                    const currentAllowances = normalizeAllowances(
                      form.getFieldValue("allowances") || []
                    );

                    const ocpiPartyIds = form.getFieldValue("ocpiPartyIds") || [];
                    if (ocpiPartyIds.length > 0) {
                      callback();
                      return;
                    }

                    if (isDiscountOnlyAllowanceSet(currentAllowances)) {
                      callback();
                      return;
                    }

                    if (!value || value.length === 0) {
                      callback(freire(texts.REQUIRED_FIELD));
                      return;
                    }

                    callback();
                  },
                },
              ],
            })(
              <Select
                mode="multiple"
                disabled={loading}
                loading={loadingStations}
                onDropdownVisibleChange={onStationsDropdownVisibleChange}
                style={{ width: "100%" }}
                placeholder={
                  loadingStations
                    ? freire(texts.CHARGING)
                    : freire(texts.SELECT_STATIONS)
                }
              >
                {stations.map((station) => (
                  <Option key={station._id} value={station.stationID}>
                    {`${station.name} (${station.stationID})`}
                  </Option>
                ))}
              </Select>
            )}
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[16, 0]}>
        <Col span={12}>
          <Form.Item
            label={freire(texts.COMPANY)}
            extra={freire(texts.COMPANY_REQUIRED_HINT)}
          >
            {getFieldDecorator("companyID", {
              rules: [
                {
                  validator: (_, value, callback) => {
                    const currentAllowances = normalizeAllowances(
                      form.getFieldValue("allowances") || DEFAULT_ALLOWANCES
                    );

                    if (currentAllowances.includes(AllowanceType.SEMI_PUBLIC) && !value) {
                      callback(freire(texts.COMPANY_REQUIRED_FOR_INTERNAL_FLEET));
                      return;
                    }

                    callback();
                  },
                },
              ],
            })(
              <Select
                disabled={loading}
                loading={loadingCompanies}
                showSearch
                optionFilterProp="children"
                placeholder={freire(texts.SELECT_COMPANY)}
              >
                {companies.map((company) => (
                  <Option key={company._id} value={company._id}>
                    {company.companyName}
                  </Option>
                ))}
              </Select>
            )}
          </Form.Item>
        </Col>
      </Row>
    </>
  );
};

export default VisibilityGroupAllowancesSection;
